/**
 * billingUpgradeController — the tiny pub/sub bridge between the AXIOS
 * response interceptor (plain JS, no React) and the mounted
 * <UpgradeDialogHost>. Mirrors coppa/ageConsentController.js's role exactly
 * (see that file's header for the fuller "why a controller, not context"
 * rationale — the interceptor runs outside React's tree and cannot call a
 * hook), but simpler: unlike the DOB-capture flow, an upgrade can NEVER be
 * resolved by a transparent retry — buying premium is an out-of-band
 * redirect-and-webhook flow, not something that resolves inline before the
 * ORIGINAL request could re-run. So there is no single-flight promise / no
 * resolve-and-retry here, only "show" (fire-and-forget) and "hide".
 *
 * SINGLE SUBSCRIBER, LATEST WINS: like the age controller, a second
 * subscribe() replaces the first (a hot-reload remount never leaks a stale
 * listener bound to an unmounted tree).
 */

let listener = null;

/**
 * Register the UI subscriber (<UpgradeDialogHost>). Returns an unsubscribe
 * fn. `handlers` is `{ onShow(details), onHide() }`.
 */
export function subscribe(handlers) {
  listener = handlers;
  return () => {
    if (listener === handlers) listener = null;
  };
}

/**
 * Raise the upgrade dialog with the refusal's own copy. `details` is
 * `{ message, nextSteps, freeAllowance, freeUsed }` — extracted by the
 * interceptor from the 402's envelope (billingApi.nextSteps /
 * meterFromError), so the dialog renders the BACKEND's own warm copy
 * verbatim rather than a second, competing hardcoded line (see
 * billing_entitlements.py `_upgrade_refusal` — the message + next_steps are
 * already growth-mindset, "what you keep" framed).
 *
 * Fire-and-forget (no promise to await, mirrors showPlatformConsent()) — the
 * calling surface's own request still rejects immediately; this only raises
 * the shared overlay on top. Idempotent: re-raising while one is already
 * open just replaces its details (the newest refusal wins).
 */
export function showUpgrade(details) {
  if (listener?.onShow) listener.onShow(details);
}

/** Hide the dialog (the host calls this on dismiss/success). */
export function hide() {
  if (listener?.onHide) listener.onHide();
}

/**
 * Test-only reset of the module singleton between cases — mirrors
 * ageConsentController's __resetForTests. Not used in production.
 */
export function __resetForTests() {
  listener = null;
}
